import { ReactNode, useMemo } from 'react'

import { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Flex, InputProps } from 'antd'
import Form, { Rule } from 'antd/es/form'
import { NamePath } from 'antd/es/form/interface'
import isNil from 'lodash.isnil'
import omit from 'lodash.omit'

import { PhoneNumberRegex, ZipCodeRegex } from 'src/utils/forms'

import { Input as GWUIInput, NumberInput as GWUINumberInput, Icon } from '../../govwell-ui'
import { Size } from '../../govwell-ui/types'
import { NumberFormat } from '../../utils/number'

import { makeFieldNameWithPrefix } from './util'
import Wrapper, { FormFieldWrapperProps } from './Wrapper'

export enum InputTypes {
  Email = 'email',
  Phone = 'tel',
  Text = 'text',
  Number = 'number',
  Currency = 'currency',
  ZipCode = 'zip-code',
}

type Props = FormFieldWrapperProps & {
  allowNegative?: boolean
  placeholder?: string
  type?: InputTypes
  prefix?: ReactNode
  suffix?: ReactNode
  suffixIcon?: IconDefinition
  prefixIcon?: IconDefinition
  size?: InputProps['size']
  min?: number
  max?: number
  id?: string
  onBlur?: (newVal?: string) => void | Promise<unknown> | Promise<void>
  noIcon?: boolean
  numberFormat?: NumberFormat
}

export function Input(props: Props) {
  const {
    allowNegative,
    ariaLabel,
    placeholder,
    type = InputTypes.Text,
    prefix,
    size = 'middle',
    min,
    max,
    onBlur,
    suffix,
    suffixIcon,
    prefixIcon,
    disabled,
    noIcon,
    numberFormat,
    rules: baseRules = [],
    ...formFieldWrapperProps
  } = props
  const { errorMessage, fieldName, fieldNamePrefix } = formFieldWrapperProps
  const getRules = (): Rule[] => {
    const required = !!errorMessage || props.required || undefined
    if (type === InputTypes.Phone) {
      return [
        ...(errorMessage
          ? [
              {
                required,
                message: errorMessage,
                validateTrigger: 'onSubmit',
              },
            ]
          : []),
        {
          pattern: PhoneNumberRegex,
          message: 'Please enter a valid phone number',
          validateTrigger: 'onSubmit',
        },
      ]
    }
    if (type === InputTypes.Email) {
      return [
        ...(errorMessage ? [{ required, message: errorMessage }] : []),
        {
          message: 'Please enter a valid email address',
          type: 'email',
          validateTrigger: 'onSubmit',
        },
      ]
    }
    if (type === InputTypes.Number || type === InputTypes.Currency) {
      return [
        ...(errorMessage
          ? [
              {
                required,
                message: errorMessage,
                validateTrigger: 'onSubmit',
                validator: (_: unknown, value: number | null | undefined) =>
                  !isNil(value) ? Promise.resolve() : Promise.reject(errorMessage),
              },
            ]
          : []),
      ]
    }
    if (type === InputTypes.ZipCode) {
      return [
        ...(errorMessage ? [{ required, message: errorMessage }] : []),
        {
          pattern: ZipCodeRegex,
          message: 'Please enter a valid zip code',
          validateTrigger: 'onSubmit',
        },
      ]
    }
    if (errorMessage) {
      return [{ required, message: errorMessage, validateTrigger: 'onSubmit' }]
    }
    return []
  }
  const rules = [...getRules(), ...baseRules]

  const getInputType = () => {
    switch (type) {
      case InputTypes.Currency: {
        return InputTypes.Number
      }
      default: {
        return type
      }
    }
  }

  const form = Form.useFormInstance()

  const leftAddon = useMemo(() => {
    if (noIcon) {
      return null
    }
    if (prefixIcon) {
      return <FontAwesomeIcon icon={prefixIcon} />
    }
    switch (type) {
      case InputTypes.Currency: {
        return undefined // NumberInput has a default leftAddon, don't override
      }
      case InputTypes.Number: {
        return undefined // NumberInput has a default leftAddon, don't override
      }
      case InputTypes.Email: {
        return <Icon.FaEnvelope />
      }
      case InputTypes.Phone: {
        return <Icon.FaPhone />
      }
      default: {
        return undefined
      }
    }
  }, [noIcon, prefixIcon, type])

  const fullyQualifiedFieldName: NamePath = useMemo(
    () => makeFieldNameWithPrefix(fieldName, fieldNamePrefix),
    [fieldName, fieldNamePrefix]
  )

  Form.useWatch(fullyQualifiedFieldName, form)
  const value = form.getFieldValue(fullyQualifiedFieldName)

  const gwuiSize: Size = size === 'small' ? 'sm' : size === 'middle' ? 'md' : 'lg'

  return (
    <Wrapper {...omit(formFieldWrapperProps, 'caption', 'label')} rules={rules} isGovWellUI>
      {({ errorMessage: errorMessageToDisplay, onValueChange, tooltip, value: wrapperValue }) => {
        const commonProps: Partial<
          React.ComponentProps<typeof GWUIInput> | React.Component<typeof GWUINumberInput>
        > = {
          caption: formFieldWrapperProps.caption,
          errorMessage: errorMessageToDisplay,
          id: formFieldWrapperProps.id,
          isDisabled: disabled,
          isInvalid: !!errorMessageToDisplay,
          isRequired: !!formFieldWrapperProps.required,
          hideRequiredAsterisk: formFieldWrapperProps.hideRequiredAsterisk,
          onBlur: async () => {
            try {
              await form.validateFields([fullyQualifiedFieldName])
            } finally {
              void onBlur?.(value)
            }
          },
          placeholder: placeholder,
          prefix: prefix,
          size: gwuiSize,
          suffix: suffix,
          suffixIcon: suffixIcon,
          tooltip,
          ...(formFieldWrapperProps.label
            ? { label: formFieldWrapperProps.label || '' }
            : { ariaLabel: ariaLabel || '' }),
          ...(noIcon ? { leftAddon: null } : { leftAddon }),
        }

        if (type === InputTypes.Currency || type === InputTypes.Number) {
          return (
            <InertFlex>
              <GWUINumberInput
                {...commonProps}
                allowNegative={allowNegative}
                format={
                  !isNil(numberFormat)
                    ? numberFormat
                    : type === InputTypes.Number
                      ? NumberFormat.Float
                      : NumberFormat.Currency
                }
                max={max}
                min={min}
                onValueChange={onValueChange}
                value={wrapperValue}
              />
            </InertFlex>
          )
        }

        return (
          <InertFlex>
            <GWUIInput
              {...commonProps}
              type={getInputType()}
              onValueChange={onValueChange}
              value={wrapperValue}
            />
          </InertFlex>
        )
      }}
    </Wrapper>
  )
}

const InertFlex = ({ children }: { children: React.ReactNode }) => <Flex>{children}</Flex>

export default Input
